import React, { useState, useEffect } from 'react';
import Trades from './Trades';
import { apiFetch } from '../api';

function Dashboard({ username, onLogout }) {
  const [state, setState] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [stateRes, eventsRes] = await Promise.all([
        apiFetch('/api/state', { reloadOnAuthError: false }),
        apiFetch('/api/events', { reloadOnAuthError: false }),
      ]);
      if (!stateRes.ok || !eventsRes.ok) {
        if (stateRes.status === 401 || eventsRes.status === 401) onLogout();
        return;
      }
      const s = await stateRes.json();
      const e = await eventsRes.json();
      setState(s);
      setEvents(e.events || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 5000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (!state) return <div className="loading">No data available.</div>;

  const position = state.position || {};
  const pnl = state.pnl || {};
  const perf = state.performance || {};
  const lastPrice = position.last_price;
  const qty = position.qty ?? 0;

  const openEvents = events.filter(ev => ev.event === 'enter_long');
  const closeEvents = events.filter(ev => ['stop_loss', 'take_profit', 'daily_loss_limit'].includes(ev.event));

  return (
    <div className="dashboard">
      <header>
        <div>
          <h1>Money Maker Mike</h1>
          <p>Live trading dashboard</p>
        </div>
        <div className="header-right">
          <span>{username}</span>
          <button onClick={onLogout}>Logout</button>
        </div>
      </header>

      <main>
        <section className="cards">
          <div className="card">
            <h3>Current price</h3>
            <p className="big">${lastPrice != null ? lastPrice.toFixed(4) : '—'}</p>
          </div>
          <div className="card">
            <h3>Position</h3>
            <p className="big">{qty > 0 ? `LONG ${qty.toFixed(4)}` : 'FLAT'}</p>
          </div>
          <div className="card">
            <h3>Daily PnL</h3>
            <p className={`big ${pnl.daily_pnl >= 0 ? 'green' : 'red'}`}>
              ${pnl.daily_pnl != null ? pnl.daily_pnl.toFixed(2) : '0.00'}
            </p>
          </div>
          <div className="card">
            <h3>Win rate</h3>
            <p className="big">{perf.win_rate != null ? `${perf.win_rate.toFixed(1)}%` : '—'}</p>
          </div>
        </section>

        <section className="details">
          <div className="detail-card">
            <h3>Position details</h3>
            <table>
              <tbody>
                <tr><td>Pair</td><td>{state.pair || '—'}</td></tr>
                <tr><td>Entry</td><td>{position.entry_price != null ? `$${position.entry_price.toFixed(4)}` : '—'}</td></tr>
                <tr><td>Stop</td><td>{position.stop_price != null ? `$${position.stop_price.toFixed(4)}` : '—'}</td></tr>
                <tr><td>Target</td><td>{position.target_price != null ? `$${position.target_price.toFixed(4)}` : '—'}</td></tr>
                <tr><td>Unrealized</td><td>{pnl.unrealized_usd != null ? `$${pnl.unrealized_usd.toFixed(2)} (${pnl.unrealized_pct?.toFixed(2) || 0}%)` : '—'}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="detail-card">
            <h3>Performance</h3>
            <table>
              <tbody>
                <tr><td>Total trades</td><td>{perf.total_trades || 0}</td></tr>
                <tr><td>Winning trades</td><td>{perf.winning_trades || 0}</td></tr>
                <tr><td>Win rate</td><td>{perf.win_rate != null ? `${perf.win_rate.toFixed(1)}%` : '—'}</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <Trades openEvents={openEvents} closeEvents={closeEvents} />
      </main>
    </div>
  );
}

export default Dashboard;
